/* eslint-disable @typescript-eslint/no-unused-vars, no-return-await, @typescript-eslint/await-thenable */
import { AxiosResponse } from 'axios';
import { action, Action, computed, Computed, createContextStore, thunk, Thunk } from 'easy-peasy';
import isError from 'lodash/isError';
import { LocalVideoTrack } from 'twilio-video';
import { VideoController } from '../../../../controllers/videoEffectsController';
import HttpClientWithRetry from '../../../../http/client/HttpClientWithRetry';
import { HttpStoreModel } from '../../../../http/models/httpModel';
import { checkVideoFrameSupport } from '../utils/videoEffectUtils';
import {
  BlurEffect,
  LocalTracks,
  PreferenceFetchStatus,
  UploadVirtualBackgroundResponse,
  VideoEffectPreExistingImages,
  VideoEffectPreferences,
  VideoEffectPreferencesPayload,
  VideoEffectState,
} from '../videoEffectTypes';

export interface VideoEffectModel {
  participantId: string | null;
  preferences: VideoEffectPreferences | null;
  preferencesStatus: PreferenceFetchStatus;
  isFetchingPreferences: Computed<VideoEffectModel, boolean>;
  preferencesError: Error | null;
  activeVideoEffect: VideoEffectState | null;
  activeBlurEffect: BlurEffect;
  activeBackgroundId: string | null;
  isVideoFrameSupported: Computed<VideoEffectModel, boolean>;
  isApplyingEffect: boolean;

  localTracks: LocalTracks | null;
  localVideoTrack: Computed<VideoEffectModel, LocalVideoTrack | null>;

  setParticipantId: Action<VideoEffectModel, string | null>;
  setPreferences: Action<VideoEffectModel, VideoEffectPreferences | null>;
  setPreferencesStatus: Action<VideoEffectModel, PreferenceFetchStatus>;
  setPreferencesError: Action<VideoEffectModel, Error | null>;
  setActiveVideoEffect: Action<VideoEffectModel, VideoEffectState | null>;
  setActiveBlurEffect: Action<VideoEffectModel, BlurEffect>;
  setActiveBackgroundId: Action<VideoEffectModel, string | null>;
  setLocalTracks: Action<VideoEffectModel, LocalTracks | null>;
  setIsApplyingEffect: Action<VideoEffectModel, boolean>;
  setRemoveCustomBackground: Action<VideoEffectModel, string>;

  fetchPreferences: Thunk<VideoEffectModel, void, void, HttpStoreModel, Promise<AxiosResponse<VideoEffectPreferences>>>;
  fetchVitualBackgrounds: Thunk<
    VideoEffectModel,
    void,
    void,
    HttpStoreModel,
    Promise<AxiosResponse<VideoEffectPreExistingImages>>
  >;
  savePreferences: Thunk<
    VideoEffectModel,
    { preferences: VideoEffectPreferencesPayload },
    void,
    HttpStoreModel,
    Promise<AxiosResponse>
  >;

  uploadBackgroundImage: Thunk<
    VideoEffectModel,
    { data: FormData },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<UploadVirtualBackgroundResponse>>
  >;
  deleteBackgroundImage: Thunk<
    VideoEffectModel,
    { imageId: string },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<UploadVirtualBackgroundResponse>>
  >;
}

const videoEffectModel = (httpClient: HttpClientWithRetry): VideoEffectModel => ({
  participantId: null,
  preferences: null,
  preferencesStatus: 'idle',
  isFetchingPreferences: computed((state) => state.preferencesStatus === 'loading'),
  preferencesError: null,
  activeVideoEffect: null,
  activeBlurEffect: 'none',
  activeBackgroundId: null,
  isVideoFrameSupported: computed(() => checkVideoFrameSupport()),
  isApplyingEffect: false,

  localTracks: null,
  localVideoTrack: computed((state) => {
    if (!state.localTracks) return null;
    const cameraTrack = state.localTracks.find((track) => track.name.includes('camera'));
    return (cameraTrack as LocalVideoTrack) || null;
  }),

  setParticipantId: action((state, participantId) => {
    state.participantId = participantId;
  }),
  setPreferences: action((state, preferences) => {
    state.preferences = preferences;
  }),
  setPreferencesStatus: action((state, status) => {
    state.preferencesStatus = status;
  }),
  setPreferencesError: action((state, error) => {
    state.preferencesError = error;
  }),
  setActiveVideoEffect: action((state, videoEffect) => {
    state.activeVideoEffect = videoEffect;
  }),
  setActiveBlurEffect: action((state, blurEffect) => {
    state.activeBlurEffect = blurEffect;
  }),
  setActiveBackgroundId: action((state, backgroundId) => {
    state.activeBackgroundId = backgroundId;
  }),
  setLocalTracks: action((state, tracks) => {
    state.localTracks = tracks;
  }),
  setIsApplyingEffect: action((state, isApplyingEffect) => {
    state.isApplyingEffect = isApplyingEffect;
  }),

  setRemoveCustomBackground: action((state, imageId) => {
    state.preferences?.participantPreference?.uploadedImages?.forEach((image) => {
      if (image.id === imageId) {
        state.preferences?.participantPreference?.uploadedImages?.splice(
          state.preferences?.participantPreference?.uploadedImages?.indexOf(image),
          1,
        );
      }
    });
  }),

  fetchPreferences: thunk(async (actions, _payload, { getState }) => {
    const { participantId } = getState();
    if (!participantId) throw new Error('participantId is required');

    actions.setPreferencesStatus('loading');
    actions.setPreferencesError(null);
    try {
      const response = await new VideoController(httpClient).fetchPreferences({ participantId });
      actions.setPreferences(response.data);
      actions.setPreferencesStatus('success');
      return response;
    } catch (err) {
      const error = isError(err) ? err : new Error(String(err));
      console.error('Failed to fetch preferences:', error);
      actions.setPreferencesStatus('error');
      actions.setPreferencesError(error);
      throw err;
    }
  }),
  fetchVitualBackgrounds: thunk(async (_actions) => {
    return await await new VideoController(httpClient).fetchVitualBackgrounds();
  }),

  savePreferences: thunk(async (_actions, payload, { getState }) => {
    const { participantId } = getState();
    if (!participantId) throw new Error('participantId is required');
    return await await new VideoController(httpClient).savePreferences({ participantId, ...payload });
  }),

  uploadBackgroundImage: thunk(async (actions, payload, { getState }) => {
    const { participantId } = getState();
    if (!participantId) throw new Error('participantId is required');
    const response = await new VideoController(httpClient).uploadBackgroundImage(participantId, payload.data);
    if (response.data?.participantPreference) {
      actions.setPreferences({ participantPreference: response.data.participantPreference });
    }
    return response;
  }),
  deleteBackgroundImage: thunk(async (actions, payload, { getState }) => {
    const { participantId } = getState();
    if (!participantId) throw new Error('participantId is required');
    const response = await new VideoController(httpClient).deleteBackgroundImage(participantId, payload.imageId);
    /* istanbul ignore next */
    if (response.data?.participantPreference) {
      actions.setPreferences({ participantPreference: response.data.participantPreference });
    }
    return response;
  }),
});

export default videoEffectModel;

export const VideoEffectStore = createContextStore<VideoEffectModel>(
  /* istanbul ignore next */
  (initialData: { httpClient: HttpClientWithRetry }) => videoEffectModel(initialData.httpClient),
  { defaultState: { httpClient: null } },
);
